import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  consumerLogin,
  consumerProfile,
  consumerSignup,
  updateConsumerProfile,
} from '../lib/api.js';

const STORAGE_KEY = 'pact-consumer-user';
const TOKEN_KEY = 'pact-consumer-token';

const ConsumerAuthContext = createContext({
  user: null,
  token: null,
  loading: false,
  login: async () => {},
  signup: async () => {},
  updateProfile: async () => {},
  logout: () => {},
});

export function ConsumerAuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(TOKEN_KEY);
  });
  const [user, setUser] = useState(() => {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.warn('Failed to parse consumer user from storage', error);
      return null;
    }
  });
  const [loading, setLoading] = useState(Boolean(token));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (user) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (token) {
      window.localStorage.setItem(TOKEN_KEY, token);
    } else {
      window.localStorage.removeItem(TOKEN_KEY);
    }
  }, [token]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const profile = await consumerProfile(token);
        setUser(profile);
      } catch (error) {
        console.error('Failed to load consumer profile', error);
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [token]);

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      login: async (credentials) => {
        const data = await consumerLogin(credentials);
        setToken(data.token);
        setUser(data.user);
        return data.user;
      },
      signup: async (profile) => {
        const data = await consumerSignup(profile);
        setToken(data.token);
        setUser(data.user);
        return data.user;
      },
      updateProfile: async (updates) => {
        if (!token) throw new Error('Not authenticated');
        const updated = await updateConsumerProfile(token, updates);
        setUser(updated);
        return updated;
      },
      logout: () => {
        setToken(null);
        setUser(null);
      },
    }),
    [user, token, loading],
  );

  return (
    <ConsumerAuthContext.Provider value={value}>{children}</ConsumerAuthContext.Provider>
  );
}

export function useConsumerAuth() {
  return useContext(ConsumerAuthContext);
}
